// pkg92 — the Help drawer (D-92a–d, h): a right-side overlay drawer opened
// from the floating "?" in AdminLayout. Articles are static markdown in
// /public/help/*.md, listed in src/help/helpIndex.js; all are fetched once on
// first open and cached for the session, so search covers full bodies and
// everything works offline afterward. Rendering is a tiny internal markdown
// subset (headings, bold, lists, links, paragraphs, inline code) parsed to
// React elements — no dangerouslySetInnerHTML, no new npm dependency, no
// package.json/lockfile churn. Context awareness (D-92d): entries whose
// `routes` substrings match the current pathname lead in a "For this page"
// card. Esc closes; full-width under 480px (D-92h).

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import HELP_INDEX from '../help/helpIndex';

const META = 'Arial, sans-serif';
const NAVY = '#0F1838';
const CREAM = '#F5F1E8';

// Session cache: id → body markdown (or null while loading / on failure).
const bodyCache = new Map();

// ── Tiny markdown subset → React elements ────────────────────────────────────
// Supported: # / ## / ### headings, "- " bullets, blank-line paragraphs,
// **bold**, *italic*, `code`, [text](href). Anything else renders as text.
function renderInline(text, keyBase, onHelpLink) {
  const out = [];
  let rest = String(text);
  let k = 0;
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/;
  while (rest) {
    const m = rest.match(pattern);
    if (!m) { out.push(rest); break; }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const tok = m[0];
    const key = `${keyBase}-${k++}`;
    if (tok.startsWith('**')) {
      out.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      out.push(<code key={key} style={{ background: CREAM, border: '1px solid #ddd', borderRadius: 4, padding: '1px 5px', fontSize: 12 }}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith('[')) {
      const mm = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      const href = mm ? mm[2] : '#';
      const label = mm ? mm[1] : tok;
      if (href.startsWith('#help:')) {
        // Cross-article link — opens inside the drawer, never navigates.
        const target = href.slice(6);
        out.push(
          <button key={key} type="button" onClick={() => onHelpLink && onHelpLink(target)}
            style={{ background: 'none', border: 'none', padding: 0, color: NAVY, textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit' }}>
            {label}
          </button>
        );
      } else {
        const external = /^https?:\/\//i.test(href);
        out.push(
          <a key={key} href={href} style={{ color: NAVY }}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
            {label}
          </a>
        );
      }
    } else { // *italic*
      out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    }
    rest = rest.slice(m.index + tok.length);
  }
  return out;
}

function renderMarkdown(md, onHelpLink) {
  const blocks = String(md || '').replace(/\r\n/g, '\n').split(/\n{2,}/);
  const els = [];
  blocks.forEach((block, bi) => {
    const lines = block.split('\n').filter((l) => l.trim() !== '');
    if (!lines.length) return;
    if (lines.every((l) => /^\s*-\s+/.test(l))) {
      els.push(
        <ul key={`b${bi}`} style={{ margin: '0 0 10px', paddingLeft: 18 }}>
          {lines.map((l, li) => (
            <li key={li} style={{ marginBottom: 3 }}>{renderInline(l.replace(/^\s*-\s+/, ''), `b${bi}l${li}`, onHelpLink)}</li>
          ))}
        </ul>
      );
      return;
    }
    const first = lines[0];
    const h = first.match(/^(#{1,3})\s+(.*)$/);
    if (h && lines.length === 1) {
      const level = h[1].length;
      els.push(
        <div key={`b${bi}`} style={{
          fontFamily: level === 1 ? 'Georgia, serif' : META,
          fontSize: level === 1 ? 17 : 13,
          fontWeight: 700, color: level === 1 ? '#111' : NAVY,
          margin: level === 1 ? '0 0 8px' : '14px 0 4px',
        }}>
          {renderInline(h[2], `b${bi}h`, onHelpLink)}
        </div>
      );
      return;
    }
    els.push(
      <p key={`b${bi}`} style={{ margin: '0 0 10px' }}>
        {lines.map((l, li) => (
          <React.Fragment key={li}>{li > 0 ? ' ' : ''}{renderInline(l, `b${bi}l${li}`, onHelpLink)}</React.Fragment>
        ))}
      </p>
    );
  });
  return els;
}

// ── Search scoring (D-92c) ───────────────────────────────────────────────────
function scoreEntry(entry, body, tokens) {
  let score = 0;
  const title = entry.title.toLowerCase();
  const summary = (entry.summary || '').toLowerCase();
  const kws = (entry.keywords || []).join(' ').toLowerCase();
  const text = (body || '').toLowerCase();
  for (const t of tokens) {
    if (!t) continue;
    if (title.includes(t)) score += 5;
    if (kws.includes(t)) score += 3;
    if (summary.includes(t)) score += 2;
    if (text.includes(t)) score += 1;
  }
  return score;
}

export default function HelpDrawer({ open, onClose, pathname }) {
  const [query, setQuery] = useState('');
  const [articleId, setArticleId] = useState(null);
  const [, setLoadTick] = useState(0); // re-render when fetches land
  const searchRef = useRef(null);

  // Fetch all article bodies once, on first open (D-92a). Failures leave the
  // entry usable via its retry path in the article view.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    HELP_INDEX.forEach((e) => {
      if (bodyCache.has(e.id)) return;
      bodyCache.set(e.id, null); // in flight
      fetch(`/help/${e.id}.md`)
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
        .then((text) => { bodyCache.set(e.id, text); if (!cancelled) setLoadTick((t) => t + 1); })
        .catch(() => { bodyCache.delete(e.id); if (!cancelled) setLoadTick((t) => t + 1); });
    });
    return () => { cancelled = true; };
  }, [open]);

  // Esc closes (article view first, then the drawer).
  const onKey = useCallback((e) => {
    if (e.key !== 'Escape') return;
    if (articleId) setArticleId(null); else onClose();
  }, [articleId, onClose]);
  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onKey]);

  // Reset the reading state each time the drawer opens; focus search.
  useEffect(() => {
    if (open) {
      setArticleId(null); setQuery('');
      setTimeout(() => { if (searchRef.current) searchRef.current.focus(); }, 50);
    }
  }, [open]);

  const contextIds = useMemo(() => {
    const path = pathname || '';
    // Specificity: the longest matching route substring wins, so on a nested
    // page (e.g. a client's Discovery Analysis) the page's own articles lead
    // instead of the generic client ones. Card capped at three.
    return HELP_INDEX
      .map((e) => ({
        e,
        best: Math.max(0, ...(e.routes || []).map((r) => {
          if (!r) return 0;
          if (r.startsWith('=')) return path === r.slice(1) ? r.length : 0; // exact
          return path.includes(r) ? r.length : 0;
        })),
      }))
      .filter((x) => x.best > 0)
      .sort((a, b) => b.best - a.best)
      .slice(0, 3)
      .map((x) => x.e.id);
  }, [pathname]);

  const results = useMemo(() => {
    const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (!tokens.length) return null; // browse mode
    return HELP_INDEX
      .map((e) => ({ e, s: scoreEntry(e, bodyCache.get(e.id), tokens) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.e);
  }, [query]); // bodyCache updates ride the loadTick re-render

  if (!open) return null;

  const article = articleId ? HELP_INDEX.find((e) => e.id === articleId) : null;
  const articleBody = article ? bodyCache.get(article.id) : undefined;

  const sections = [];
  HELP_INDEX.forEach((e) => {
    if (!sections.includes(e.section)) sections.push(e.section);
  });

  function Item({ entry }) {
    return (
      <button type="button" onClick={() => setArticleId(entry.id)}
        style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderRadius: 5, padding: '7px 8px', cursor: 'pointer', fontFamily: META }}
        onMouseOver={(e) => { e.currentTarget.style.background = '#f0f2f8'; }}
        onMouseOut={(e) => { e.currentTarget.style.background = 'none'; }}>
        <span style={{ fontSize: 13, color: NAVY, fontWeight: 600 }}>{entry.title}</span>
        <span style={{ display: 'block', fontSize: 11.5, color: '#777', marginTop: 1, lineHeight: 1.4 }}>{entry.summary}</span>
      </button>
    );
  }

  return (
    <>
      {/* click-away scrim */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,24,56,0.25)', zIndex: 1200 }} />
      <div role="dialog" aria-label="Help" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1201,
        width: window.innerWidth < 480 ? '100%' : 340,
        background: '#fff', borderLeft: '1px solid #c9cfdd',
        boxShadow: '-6px 0 18px rgba(15,24,56,0.15)',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ background: NAVY, color: '#fff', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'Georgia, serif', fontSize: 15 }}>Help</span>
          <button type="button" onClick={onClose} aria-label="Close help"
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#c9cfdd', fontSize: 16, cursor: 'pointer' }}>✕</button>
        </div>

        {!article && (
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #e4e9f0' }}>
            <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={'Search help — try "credits" or "trust"'}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #c9cfdd', borderRadius: 5, fontSize: 13, fontFamily: META }} />
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', padding: '10px 12px', fontFamily: META }}>
          {article ? (
            <>
              <button type="button" onClick={() => setArticleId(null)}
                style={{ background: 'none', border: 'none', color: NAVY, fontSize: 12.5, cursor: 'pointer', padding: 0, margin: '0 0 8px', textDecoration: 'underline', fontFamily: META }}>
                ← {query ? `All results for "${query}"` : 'All topics'}
              </button>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: 17, color: '#111', margin: '0 0 2px' }}>{article.title}</div>
              <div style={{ fontSize: 10.5, letterSpacing: 0.6, textTransform: 'uppercase', color: '#888', margin: '0 0 10px' }}>{article.section}</div>
              <div style={{ fontSize: 13, color: '#222', lineHeight: 1.6 }}>
                {articleBody ? renderMarkdown(articleBody, (id) => setArticleId(id)) : articleBody === null ? (
                  <p style={{ color: '#888' }}>Loading…</p>
                ) : (
                  <p style={{ color: '#888' }}>
                    This article couldn't load.{' '}
                    <button type="button" style={{ background: 'none', border: 'none', color: NAVY, cursor: 'pointer', padding: 0, textDecoration: 'underline', fontSize: 13, fontFamily: META }}
                      onClick={() => { bodyCache.delete(article.id); setArticleId(null); setTimeout(() => setArticleId(article.id), 0); }}>
                      Try again
                    </button>
                  </p>
                )}
              </div>
            </>
          ) : results ? (
            <>
              <div style={{ fontSize: 10.5, letterSpacing: 0.6, textTransform: 'uppercase', color: '#888', margin: '2px 0 6px' }}>
                {results.length ? `Results (${results.length})` : 'No results'}
              </div>
              {results.map((e) => <Item key={e.id} entry={e} />)}
              {!results.length && (
                <p style={{ fontSize: 12.5, color: '#777' }}>Nothing matched — try a different word, or browse all topics by clearing the search.</p>
              )}
            </>
          ) : (
            <>
              {contextIds.length > 0 && (
                <div style={{ background: CREAM, border: '1px solid #dfd8c8', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
                  <div style={{ fontSize: 10.5, letterSpacing: 0.6, textTransform: 'uppercase', color: '#8a6d3b', margin: '0 0 6px' }}>For this page</div>
                  {contextIds.map((id) => {
                    const e = HELP_INDEX.find((x) => x.id === id);
                    return e ? <Item key={id} entry={e} /> : null;
                  })}
                </div>
              )}
              {sections.map((sec) => (
                <React.Fragment key={sec}>
                  <div style={{ fontSize: 10.5, letterSpacing: 0.6, textTransform: 'uppercase', color: '#888', margin: '10px 0 6px' }}>{sec}</div>
                  {HELP_INDEX.filter((e) => e.section === sec).map((e) => <Item key={e.id} entry={e} />)}
                </React.Fragment>
              ))}
            </>
          )}
        </div>

        <div style={{ borderTop: '1px solid #e4e9f0', padding: '9px 12px', fontSize: 11.5, color: '#888', fontFamily: META }}>
          Can't find it? <a href="mailto:mail@lawmighty.com" style={{ color: NAVY }}>Email support</a>
        </div>
      </div>
    </>
  );
}
